import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  Instagram,
  Facebook,
  Utensils,
  Home,
  MapPin,
  Store,
  Info,
  LogIn,
} from 'lucide-react';
import Logo from 'components/layout/Logo';
import SiteCredit from 'components/layout/SiteCredit';
import { useContent } from 'context/ContentContext';
import { useAuth } from 'context/AuthContext';

const FooterContainer = styled.footer`
  background-color: ${(props) => props.theme.colors.primary};
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layouts.pagePadding};
  display: flex;
  flex-direction: column;
  gap: 4rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    gap: 2rem;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 0 4rem;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 4rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    gap: 1rem;
    width: auto;
  }
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  transition: ${(props) => props.theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.offWhite};
  padding: 0.75rem;
  min-height: 44px;
  font-size: 1.6rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 0;
    min-height: auto;
    font-size: inherit;

    :hover {
      color: ${({ theme }) => theme.colors.yellow};
    }
  }
`;

const SocialContainer = styled.div`
  display: flex;
  gap: 3rem;
  justify-content: center;
  align-items: center;

  ${({ theme }) => theme.mediaQueries.sm} {
    gap: 2rem;
  }
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.colors.offWhite};
  transition: ${(props) => props.theme.transitions.default};
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 0;
    min-width: auto;
    min-height: auto;

    :hover {
      color: ${({ theme }) => theme.colors.yellow};
      transform: scale(1.2);
    }
  }
`;

const CopyrightSection = styled.div`
  color: ${({ theme }) => theme.colors.offWhite};
  text-align: center;
  font-size: 1rem;

  p,
  a {
    ${({ theme }) => theme.mixins.textP3};
    font-size: 1rem;
  }

  a {
    text-decoration: underline;
    transition: ${(props) => props.theme.transitions.default};
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    p,
    a {
      font-size: 1.2rem;
    }

    a:hover {
      color: ${({ theme }) => theme.colors.yellow};
    }
  }
`;

const Footer = ({ onSectionChange }) => {
  const { logo = {}, social = {}, messages = {} } = useContent();
  const { user } = useAuth();

  const navIconSize = 20;
  const socialIconSize = 24;

  const navIcons = {
    hero: <Home size={navIconSize} />,
    menu: <Utensils size={navIconSize} />,
    location: <MapPin size={navIconSize} />,
    about: <Store size={navIconSize} />,
  };

  const getIconForNavItem = (id) => navIcons[id] || <Info size={navIconSize} />;

  // Build nav items with translated labels
  const navItems = [
    { id: 'hero', label: messages?.navigation?.home || 'Home' },
    { id: 'menu', label: messages?.navigation?.menu || 'Menu' },
    { id: 'location', label: messages?.navigation?.location || 'Location' },
    { id: 'about', label: messages?.navigation?.about || 'About' },
  ];

  return (
    <FooterContainer>
      <Container>
        <FlexContainer>
          <Logo logoSrc={logo.whiteLogo || ''} />
          <NavList>
            {navItems.map(({ id, label }) => (
              <li key={id}>
                <NavButton onClick={() => onSectionChange(id)}>
                  {getIconForNavItem(id)}
                  {label}
                </NavButton>
              </li>
            ))}
            {!user && (
              <li key="login">
                <NavButton onClick={() => (window.location.href = '/login')}>
                  <LogIn size={navIconSize} />
                  {messages.navigation?.login || 'Login'}
                </NavButton>
              </li>
            )}
          </NavList>
          <SocialContainer>
            <SocialLink
              href={social.instagram}
              aria-label={messages.ariaLabels?.instagram || 'Instagram'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram size={socialIconSize} />
            </SocialLink>
            <SocialLink
              href={social.facebook}
              aria-label={messages.ariaLabels?.facebook || 'Facebook'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook size={socialIconSize} />
            </SocialLink>
            <SocialLink
              href={social.yelp}
              aria-label={messages.ariaLabels?.yelp || 'Yelp'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Utensils size={socialIconSize} />
            </SocialLink>
          </SocialContainer>
        </FlexContainer>
        <CopyrightSection>
          <p>&copy; {new Date().getFullYear()} Birreria El Semaforo.</p>
          <SiteCredit />
        </CopyrightSection>
      </Container>
    </FooterContainer>
  );
};

Footer.propTypes = {
  onSectionChange: PropTypes.func.isRequired,
};

export default Footer;
